/**
 * Dialog for editing a canonical piece.
 * Allows converting between freetext and Wikidata, or updating existing data.
 */

import { useState, useEffect } from 'react'
import { useUpdateCanonicalPiece } from '../../hooks/useCanonicalPieces'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Loader2, Info } from 'lucide-react'
import { useToast } from '../../hooks/use-toast'
import { getComposerById, getWorkById } from '../../services/api/wikidata'
import type { CanonicalPiece } from '../../types/canonical-piece'

interface EditCanonicalPieceDialogProps {
  canonicalPiece: CanonicalPiece
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function EditCanonicalPieceDialog({
  canonicalPiece,
  open,
  onOpenChange,
  onSuccess,
}: EditCanonicalPieceDialogProps) {
  const updateMutation = useUpdateCanonicalPiece()
  const { toast } = useToast()

  // Form state
  const [composerWikidataId, setComposerWikidataId] = useState(
    canonicalPiece.composer_wikidata_id || '',
  )
  const [composerName, setComposerName] = useState(canonicalPiece.composer_name || '')
  const [workWikidataId, setWorkWikidataId] = useState<string>(
    canonicalPiece.work_wikidata_id || '',
  )
  const [workTitle, setWorkTitle] = useState<string>(canonicalPiece.work_title || '')
  const [isFetchingComposer, setIsFetchingComposer] = useState(false)
  const [isFetchingWork, setIsFetchingWork] = useState(false)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setComposerWikidataId(canonicalPiece.composer_wikidata_id || '')
      setComposerName(canonicalPiece.composer_name || '')
      setWorkWikidataId(canonicalPiece.work_wikidata_id || '')
      setWorkTitle(canonicalPiece.work_title || '')
    }
  }, [open, canonicalPiece])

  // Auto-fetch composer name when Wikidata ID changes
  useEffect(() => {
    const fetchComposerName = async () => {
      const trimmedId = composerWikidataId.trim()
      if (!trimmedId || !trimmedId.match(/^Q\d+$/i)) {
        return
      }

      setIsFetchingComposer(true)
      try {
        const composer = await getComposerById(trimmedId)
        setComposerName(composer.name)
      } catch (error) {
        console.error('Failed to fetch composer:', error)
        toast({
          title: 'Warning',
          description: `Could not fetch composer details for ${trimmedId}`,
          variant: 'destructive',
        })
      } finally {
        setIsFetchingComposer(false)
      }
    }

    const timeoutId = setTimeout(fetchComposerName, 500)
    return () => clearTimeout(timeoutId)
  }, [composerWikidataId, toast])

  // Auto-fetch work title when Wikidata ID changes
  useEffect(() => {
    const fetchWorkTitle = async () => {
      const trimmedWorkId = workWikidataId.trim()

      if (!trimmedWorkId || !trimmedWorkId.match(/^Q\d+$/i)) {
        return
      }

      setIsFetchingWork(true)
      try {
        const work = await getWorkById(trimmedWorkId)
        setWorkTitle(work.title)
      } catch (error) {
        console.error('Failed to fetch work:', error)
        toast({
          title: 'Warning',
          description: `Could not fetch work details for ${trimmedWorkId}`,
          variant: 'destructive',
        })
      } finally {
        setIsFetchingWork(false)
      }
    }

    const timeoutId = setTimeout(fetchWorkTitle, 500)
    return () => clearTimeout(timeoutId)
  }, [workWikidataId, composerWikidataId, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate: Must have either composer ID or composer name
    const hasComposer = composerWikidataId.trim() || composerName.trim()
    if (!hasComposer) {
      toast({
        title: 'Validation Error',
        description: 'Either Composer Wikidata ID or Composer Name is required',
        variant: 'destructive',
      })
      return
    }

    // Validate: Must have either work ID or work title
    const hasWork = workWikidataId.trim() || workTitle.trim()
    if (!hasWork) {
      toast({
        title: 'Validation Error',
        description: 'Either Work Wikidata ID or Work Title is required',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateMutation.mutateAsync({
        canonicalPieceId: canonicalPiece.canonical_piece_id,
        input: {
          composer_wikidata_id: composerWikidataId.trim() || null,
          composer_name: composerName.trim() || null,
          work_wikidata_id: workWikidataId.trim() || null,
          work_title: workTitle.trim() || null,
        },
      })

      toast({
        title: 'Success',
        description: 'Canonical piece updated successfully',
      })

      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update canonical piece',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Canonical Piece</DialogTitle>
          <DialogDescription>
            Update composer and work information. You can use Wikidata IDs (which auto-fill names)
            or enter freetext names directly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Required:</strong> Either Composer Wikidata ID or Composer Name (or both)
              <br />
              <strong>Required:</strong> Either Work Wikidata ID or Work Title (or both)
              <br />
              <strong>Auto-fill:</strong> When you enter a Wikidata ID, the name will be fetched
              automatically.
            </AlertDescription>
          </Alert>

          {/* Composer Section */}
          <div className="space-y-2">
            <Label htmlFor="composer-wikidata-id">Composer Wikidata ID</Label>
            <div className="relative">
              <Input
                id="composer-wikidata-id"
                value={composerWikidataId}
                onChange={(e) => setComposerWikidataId(e.target.value)}
                placeholder="Q7304 (optional if name provided)"
                disabled={isFetchingComposer}
              />
              {isFetchingComposer && (
                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Wikidata QID for the composer. Name will auto-fill when you enter a valid ID.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="composer-name">Composer Name</Label>
            <Input
              id="composer-name"
              value={composerName}
              onChange={(e) => setComposerName(e.target.value)}
              placeholder="Gustav Mahler (or use Wikidata ID above)"
              disabled={isFetchingComposer}
            />
            <p className="text-xs text-muted-foreground">
              {isFetchingComposer
                ? 'Fetching composer name from Wikidata...'
                : 'Auto-filled from Wikidata ID, or enter manually'}
            </p>
          </div>

          {/* Work Section */}
          <div className="pt-4 border-t space-y-4">
            <div className="space-y-2">
              <Label htmlFor="work-wikidata-id">Work Wikidata ID</Label>
              <div className="relative">
                <Input
                  id="work-wikidata-id"
                  value={workWikidataId}
                  onChange={(e) => setWorkWikidataId(e.target.value)}
                  placeholder="Q123456 (optional if title provided)"
                  disabled={isFetchingWork}
                />
                {isFetchingWork && (
                  <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Wikidata QID for the work. Title will auto-fill when you enter a valid ID.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="work-title">Work Title</Label>
              <Input
                id="work-title"
                value={workTitle}
                onChange={(e) => setWorkTitle(e.target.value)}
                placeholder="Symphony No. 5 (or use Wikidata ID above)"
                disabled={isFetchingWork}
              />
              <p className="text-xs text-muted-foreground">
                {isFetchingWork
                  ? 'Fetching work title from Wikidata...'
                  : 'Auto-filled from Wikidata ID, or enter manually'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
